<template>
  <div>
    <!-- use placeholder when no code is retrieved, considering English default language until be is done -->
    <BbSelectList v-model="selectedLanguage" :placeholder="t('translation.english')">
      <template #default>
        <BbSelectListItem
          v-for="item in availableTranslations"
          :key="`select-6-${item.language}`"
          :label="item.name"
          :value="item.language"
          :disabled="isTranslationPending(item.status)"
        >
          <div class="status-container">
            <BbIcon v-if="selectedLanguage === item.language" size="small" name="checkmark-small" />
            <BbIcon v-else-if="isTranslationPending(item.status)" class="loading-icon" size="small" name="spin" />
            <div class="bb-select-list-item__label">
              {{ item.name }}
            </div>
          </div>
          <div class="bb-select-list-item__description">
            {{ item.original ? t('translation.original') : item.status }}
          </div>
        </BbSelectListItem>
      </template>
    </BbSelectList>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import useTranslation from '~/composables/useTranslation';
import useDocuments from '~/composables/useDocuments';
import { TRANSLATION_STATUS, TRANSLATION_TOPIC_CHANNEL } from '~/constants/translations';

export default {
  setup() {
    const { t } = useI18n();
    const { selectedReference } = useDocuments();
    const {
      currentLanguageCode,
      getAvailableTranslations,
      setCurrentPdfLanguage,
      subscribeToDocumentTranslationChannel,
      unsubscribeFromDocumentTranslationChannel,
    } = useTranslation();
    const availableTranslations = ref([]);
    const selectedLanguage = ref(null);
    const originalLanguage = ref('');
    const isTranslationPending = (status) => status === TRANSLATION_STATUS.pending;

    const updateAvailableTranslations = async () => {
      const { languages } = await getAvailableTranslations(selectedReference.value);
      availableTranslations.value = languages;
      return languages;
    };

    onMounted(async () => {
      const languages = await updateAvailableTranslations();
      originalLanguage.value = languages.find(({ original }) => original).language;
      selectedLanguage.value = currentLanguageCode.value || originalLanguage.value;

      subscribeToDocumentTranslationChannel(selectedReference.value.externalId, TRANSLATION_TOPIC_CHANNEL.done, () => {
        updateAvailableTranslations();
      });
      subscribeToDocumentTranslationChannel(
        selectedReference.value.externalId,
        TRANSLATION_TOPIC_CHANNEL.initiated,
        () => {
          updateAvailableTranslations();
        },
      );
    });

    onUnmounted(() => {
      unsubscribeFromDocumentTranslationChannel(
        selectedReference.value.externalId,
        TRANSLATION_TOPIC_CHANNEL.done,
        () => {
          updateAvailableTranslations();
        },
      );
      unsubscribeFromDocumentTranslationChannel(
        selectedReference.value.externalId,
        TRANSLATION_TOPIC_CHANNEL.initiated,
        () => {
          updateAvailableTranslations();
        },
      );
    });

    watch(selectedLanguage, (newVal) => {
      if (newVal === originalLanguage.value) {
        setCurrentPdfLanguage('');
        return;
      }
      setCurrentPdfLanguage(newVal);
    });

    return {
      availableTranslations,
      isTranslationPending,
      selectedLanguage,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.bb-select-list {
  :deep(.el-select .el-select__wrapper .el-select__selection .el-select__placeholder) {
    color: $bb-neutral-1;
  }

  .el-select-dropdown__item {
    flex-direction: column;
    align-items: flex-start !important;
    padding: $bb-spacing-1 $bb-spacing-3 !important;
  }

  .status-container {
    display: flex;
    gap: $bb-spacing-1;

    // rotate the waiting icon
    @-webkit-keyframes spin {
      from {
        -webkit-transform: rotate(0deg);
      }
      to {
        -webkit-transform: rotate(360deg);
      }
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .loading-icon {
      display: inline-block;
      -webkit-animation: spin 2s linear infinite;
      animation: spin 2s linear infinite;
    }
  }

  .bb-select-list-item {
    &__label {
      @include bb-regular-text-styles('md');
      color: $bb-neutral-1;
    }

    &__description {
      @include bb-regular-text-styles('sm');
      color: $bb-neutral-2;
    }
  }
}
</style>
